import React, { useEffect, useState } from 'react';
import {
    Box, Stack, TextField, Typography, Button, Select, MenuItem, FormControl,
    InputLabel, FormControlLabel, Switch, Divider
} from '@mui/material';

import dayjs from 'dayjs';
import TariffService from '../TariffService';
import RatingService from '../RatingService';
import DecimalInput from '../UiComponents/DecimalInput';
import SubMeterInputs from './SubMeterInputs';

const FreeConsumptionCalculator = () => {
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [tariffId, setTariffId] = useState('');
    const [tariffs, setTariffs] = useState([]);
    const [indexTypes, setIndexTypes] = useState([]);
    const [mainMeterConsumptions, setMainMeterConsumptions] = useState({});
    const [contractPowerChargeSharing, setContractPowerChargeSharing] = useState(null);
    const [exceededPowerChargeSharing, setExceededPowerChargeSharing] = useState(null);
    const [subsidized, setSubsidized] = useState(true);
    const [subMeters, setSubMeters] = useState([]);

    useEffect(() => {
        TariffService.getTariffs().then(res => setTariffs(res.data));
    }, []);

    useEffect(() => {
        if (tariffId) {
            TariffService.getInvoiceIndexTypesByTariffId(tariffId).then(res => {
                setIndexTypes(res.data);
                setMainMeterConsumptions({});
            });
        }
    }, [tariffId]);

    const handleMainMeterInputChange = (type, value) => {
        setMainMeterConsumptions(prev => ({ ...prev, [type]: value }));
    };

    const handleAddSubMeter = () => {
        setSubMeters([...subMeters, {
            subMeterId: '',
            priority: 1,
            consumptions: {}
        }]);
    };

    const handleSubMeterChange = (index, key, value) => {
        const updated = [...subMeters];
        if (key === 'subMeterId' && updated.some((m, i) => i !== index && m.subMeterId === value)) {
            alert("Aynı sayaç kodu iki kez girilemez!");
            return;
        }
        if (key === 'priority') {
            updated[index][key] = value;
        } else if (key === 'subMeterId') {
            updated[index].subMeterId = value;
        } else {
            updated[index].consumptions[key] = value;
        }
        setSubMeters(updated);
    };

    const buildRequest = () => {
        const priorityGroups = {};
        subMeters.forEach(meter => {
            if (!priorityGroups[meter.priority]) {
                priorityGroups[meter.priority] = [];
            }
            priorityGroups[meter.priority].push(meter);
        });

        const consumptionListByPriorityDtoos = [];

        // Alt sayaçlı veriler
        Object.entries(priorityGroups).forEach(([priority, meters]) => {
            const indexGroups = {};
            meters.forEach(meter => {
                Object.entries(meter.consumptions).forEach(([type, value]) => {
                    if (!indexGroups[type]) indexGroups[type] = [];
                    indexGroups[type].push({ subMeterId: meter.subMeterId, consumption: parseFloat(value) });
                });
            });

            const consumptions = Object.entries(indexGroups).map(([type, shares]) => {
                const total = shares.reduce((sum, s) => sum + s.consumption, 0);
                return {
                    indexType: type,
                    consumption: priority === '1' ? parseFloat(mainMeterConsumptions[type] || 0) : total,
                    subMeterShares: priority === '1' ? shares : shares.length > 0 ? shares : undefined
                };
            });

            if (priority === '1') {
                Object.entries(mainMeterConsumptions).forEach(([type, val]) => {
                    const alreadyExists = consumptions.find(c => c.indexType === type);
                    if (!alreadyExists && parseFloat(val) > 0) {
                        consumptions.push({
                            indexType: type,
                            consumption: parseFloat(val),
                            subMeterShares: [] // Ana sayaç tüketimi olduğu için boş
                        });
                    }
                });
            }


            if (consumptions.length > 0) {
                consumptionListByPriorityDtoos.push({
                    priority: parseInt(priority),
                    consumptions: consumptions.filter(c => c.consumption > 0)
                });
            }
        });

        // Alt sayaç hiç yoksa ama ana sayaçtan veri varsa
        if (subMeters.length === 0 && Object.keys(mainMeterConsumptions).length > 0) {
            const mainConsumptions = Object.entries(mainMeterConsumptions).map(([type, val]) => ({
                indexType: type,
                consumption: parseFloat(val),
                subMeterShares: [] // boş array gönderilmeli
            }));

            consumptionListByPriorityDtoos.push({
                priority: 1,
                consumptions: mainConsumptions.filter(c => c.consumption > 0)
            });
        }

        return {
            startDate: dayjs(startDate).format('YYYY-MM-DD'),
            endDate: dayjs(endDate).format('YYYY-MM-DD'),
            tariffId,
            subsidized,
            contractPowerChargeSharing,
            exceededPowerChargeSharing,
            consumptionListByPriorityDtoos
        };
    };

    const handleSubmit = async () => {
        const req = buildRequest();
        const response = await RatingService.rating(req);
        console.log('Rating result:', response.data);
        alert("Tüketim hesaplama tamamlandı. Sonuçlar konsola yazıldı.");
    };

    return (
        <Box sx={{ p: 4 }}>
            <Typography variant="h5">Ücretsiz Tüketim Hesaplama</Typography>
            <Stack spacing={3}>

                <Stack direction="row" spacing={2}>
                    <TextField
                        label="Başlangıç Tarihi"
                        type="date"
                        fullWidth
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        label="Bitiş Tarihi"
                        type="date"
                        fullWidth
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                </Stack>

                <FormControl fullWidth>
                    <InputLabel>Tarife</InputLabel>
                    <Select value={tariffId} onChange={(e) => setTariffId(e.target.value)}>
                        {tariffs.map(t => <MenuItem key={t.tariffId} value={t.tariffId}>{t.name}</MenuItem>)}
                    </Select>
                </FormControl>

                <FormControlLabel
                    label="Sübvansiyondan faydalanıyor"
                    control={<Switch checked={subsidized} onChange={(e) => setSubsidized(e.target.checked)} />}
                />

                {indexTypes.map(it => (
                    <DecimalInput
                        key={it.indexType}
                        label={`Ana Sayaç - ${it.indexName}`}
                        value={mainMeterConsumptions[it.indexType] || ''}
                        onChange={(val) => handleMainMeterInputChange(it.indexType, val)}
                        decimalSeparator="," maxDecimalDigits={3} unit={it.unit}
                    />
                ))}

                {indexTypes.find(i => i.indexType === 'P') && (
                    <FormControl fullWidth>
                        <InputLabel>Sözleşme Bedelini Kim Ödesin?</InputLabel>
                        <Select value={contractPowerChargeSharing || ''} onChange={(e) => setContractPowerChargeSharing(e.target.value)}>
                            <MenuItem value="MAIN_METER_ONLY">Ana sayaç</MenuItem>
                            <MenuItem value="SUB_METERS_ONLY">Süzme sayaçlar eşit paylaşır</MenuItem>
                            <MenuItem value="SHARED_BETWEEN_ALL">Hepsi eşit paylaşır</MenuItem>
                        </Select>
                    </FormControl>
                )}

                {indexTypes.find(i => i.indexType === 'D') && (
                    <FormControl fullWidth>
                        <InputLabel>Güç Aşım Bedelini Kim Ödesin?</InputLabel>
                        <Select value={exceededPowerChargeSharing || ''} onChange={(e) => setExceededPowerChargeSharing(e.target.value)}>
                            <MenuItem value="MAIN_METER_ONLY">Ana sayaç</MenuItem>
                            <MenuItem value="SUB_METERS_ONLY">Süzme sayaçlar eşit paylaşır</MenuItem>
                            <MenuItem value="SHARED_BETWEEN_ALL">Hepsi eşit paylaşır</MenuItem>
                        </Select>
                    </FormControl>
                )}

                <Divider />
                <Typography variant="h6">Süzme Sayaçlar</Typography>

                {subMeters.map((meter, index) => (
                    <SubMeterInputs
                        key={index}
                        meter={meter}
                        index={index}
                        indexTypes={indexTypes}
                        onChange={handleSubMeterChange}
                        subMeters={subMeters}
                    />
                ))}

                <Button onClick={handleAddSubMeter} variant="outlined">Süzme Sayaç Ekle</Button>
                <Button onClick={handleSubmit} variant="contained" color="primary">Tüketimi Hesapla</Button>
            </Stack>
        </Box>
    );
};

export default FreeConsumptionCalculator;
